import * as React from 'react';
import { Check } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import { onInnerControl } from './inner-control';
import {
  iconFactRecipe,
  keyValueRowRecipe,
  listCheckRecipe,
  listItemRecipe,
  listRecipe,
  listRowRecipe,
  type IconFactRecipeProps,
  type KeyValueRowRecipeProps,
  type ListItemRecipeProps,
  type ListRecipeProps,
  type ListRowRecipeProps,
} from '../recipes/list.recipe';
import { uiMarkerInternal } from '../recipes/shared';
import { DataValue, type ValueFormat } from './DataValue';
import { Icon, IconTile, type IconTileProps } from './Icon';
import { Text } from './Text';

type ListMarker = NonNullable<ListRecipeProps['marker']>;
const ListContext = React.createContext<ListMarker>('bullet');

export interface ListProps
  extends React.HTMLAttributes<HTMLUListElement>,
    ListRecipeProps {}

/**
 * Words in a list.
 *
 *   <List marker="check">
 *     <ListItem>Unlimited drivers</ListItem>
 *     <ListItem>Priority support</ListItem>
 *   </List>
 *
 * `number` renders an <ol>; everything else a <ul>. The plan cards, the
 * onboarding steps and the connector instructions each had one of these,
 * built from a ul with `space-y-2` and a Check icon pasted into every li.
 */
export const List = React.forwardRef<HTMLUListElement, ListProps>(function List(
  { marker = 'bullet', gap, size, tone, className, ...props },
  ref
) {
  const Component = (marker === 'number' ? 'ol' : 'ul') as 'ul';
  return (
    <ListContext.Provider value={marker ?? 'bullet'}>
      <Component
        ref={ref}
        className={cn(listRecipe({ marker, gap, size, tone }), className)}
        {...props}
      />
    </ListContext.Provider>
  );
});

export interface ListItemProps extends React.LiHTMLAttributes<HTMLLIElement> {
  /** Replace the tick, for a `check` list that wants a different glyph. */
  icon?: LucideIcon;
  /**
   * This item's colour, over the list's — a rule in a checklist that is met
   * (`success`) while the rest stay `muted`. Inherits the list's by default.
   */
  tone?: ListItemRecipeProps['tone'];
}

export const ListItem = React.forwardRef<HTMLLIElement, ListItemProps>(
  function ListItem({ icon, tone, className, children, ...props }, ref) {
    const marker = React.useContext(ListContext);
    return (
      <li
        ref={ref}
        className={cn(listItemRecipe({ marker, tone }), className)}
        {...props}
      >
        {marker === 'check' ? (
          <Icon icon={icon ?? Check} size="sm" className={listCheckRecipe()} />
        ) : null}
        {marker === 'check' ? (
          <span className={uiMarkerInternal}>{children}</span>
        ) : (
          children
        )}
      </li>
    );
  }
);

export interface KeyValueRowProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    KeyValueRowRecipeProps {
  label: React.ReactNode;
  /** The value. Goes through DataValue when `format` is set. */
  value?: React.ReactNode;
  format?: ValueFormat;
  /** Subtotals and the total: the row carries the weight. */
  emphasis?: boolean;
  tone?: 'default' | 'muted' | 'destructive' | 'success';
  /** Render nothing when the value is empty, instead of a placeholder. */
  hideEmpty?: boolean;
  children?: React.ReactNode;
}

/**
 * A label on the left, its value on the right.
 *
 *   <Stack divided>
 *     <KeyValueRow label="Subtotal" value={1200} format="currency" />
 *     <KeyValueRow label="Tax" value={96} format="currency" />
 *     <KeyValueRow label="Total" value={1296} format="currency" emphasis highlight />
 *   </Stack>
 *
 * The horizontal counterpart to FieldValue, which stacks the label over the
 * value. The auction drawer, the invoice totals and the impound fee table
 * each drew this with their own padding and their own idea of bold.
 */
export const KeyValueRow = React.forwardRef<HTMLDivElement, KeyValueRowProps>(
  function KeyValueRow(
    {
      label,
      value,
      format,
      emphasis = false,
      tone = 'default',
      hideEmpty = false,
      density,
      inset,
      highlight,
      className,
      children,
      ...props
    },
    ref
  ) {
    const content = children ?? value;
    const empty = content === null || content === undefined || content === '';
    if (hideEmpty && empty) return null;
    return (
      <div
        ref={ref}
        className={cn(
          keyValueRowRecipe({ density, inset, highlight }),
          className
        )}
        {...props}
      >
        <Text
          variant={emphasis ? 'body-sm-strong' : 'body-sm'}
          tone={emphasis ? 'default' : 'muted'}
          as="span"
          className={format ? 'min-w-0' : 'shrink-0'}
        >
          {label}
        </Text>
        <Text
          variant={emphasis ? 'body-sm-strong' : 'body-sm'}
          tone={tone}
          as="span"
          align="right"
          numeric={Boolean(format)}
          className={format ? 'shrink-0' : 'min-w-0 break-words'}
        >
          {format ? (
            <DataValue
              value={content as string | number | null}
              format={format}
            />
          ) : (
            content
          )}
        </Text>
      </div>
    );
  }
);

export interface ListRowProps
  extends React.HTMLAttributes<HTMLElement>,
    ListRowRecipeProps {
  /** A button when the row is the thing you click; a div when it holds controls. */
  as?: 'div' | 'button';
  disabled?: boolean;
  /**
   * The row is the thing you choose. As a button, a click. As a div, the
   * row takes a button's role, a tab stop, and Enter and Space, so a row
   * that holds a control (a status pill) is still a button to the keyboard.
   * A click or a keypress on a control inside the row is for that control.
   */
  onSelect?: () => void;
}

/**
 * One row in a list of things — a thumbnail, a couple of lines, a control on
 * the right — inside a `<Stack divided>`. `shape="band"` is the row of a
 * grouped list column (see ListBand); `inset="y"` and `"between"` are rows
 * inside a card that already carries the side inset; `dropTarget` marks
 * where a dragged row would land.
 *
 *   <Stack divided>
 *     {invoices.map((inv) => (
 *       <ListRow key={inv.id} interactive as="button" onClick={…}>
 *         <Thumbnail src={inv.photo} />
 *         <Stack gap={0} grow>
 *           <Text variant="body-sm-strong" truncate>{inv.title}</Text>
 *           <Text variant="caption" tone="muted" truncate>{inv.number}</Text>
 *         </Stack>
 *         <Badge>{inv.status}</Badge>
 *       </ListRow>
 *     ))}
 *   </Stack>
 *
 * With `onSelect` and `as="div"` the row is a button to the keyboard and
 * the screen reader while still able to hold a control, the same way
 * OptionCard does it.
 */
export const ListRow = React.forwardRef<HTMLElement, ListRowProps>(
  function ListRow(
    {
      as = 'div',
      inset,
      shape,
      dropTarget,
      interactive,
      selected,
      wrap,
      disabled,
      onSelect,
      onClick,
      onKeyDown,
      className,
      ...props
    },
    ref
  ) {
    const variants = { inset, shape, dropTarget, interactive, selected, wrap };
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      onClick?.(event);
      if (!onSelect || event.defaultPrevented || disabled) return;
      if (onInnerControl(event.currentTarget, event.target)) return;
      onSelect();
    };
    if (as === 'button') {
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
          disabled={disabled}
          className={cn(listRowRecipe(variants), className)}
          onClick={handleClick}
          onKeyDown={onKeyDown}
          {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        />
      );
    }
    const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
      onKeyDown?.(event);
      if (!onSelect || event.defaultPrevented || disabled) return;
      // Only when the row itself has focus: a control inside it that
      // receives Space must keep it.
      if (event.target !== event.currentTarget) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect();
      }
    };
    const selectable = onSelect
      ? { role: 'button', tabIndex: disabled ? -1 : 0 }
      : {};
    // Cast outside the tag: a generic's `>` inside the attributes is where the
    // plain-div overlay test stops reading them.
    const divRef = ref as React.Ref<HTMLDivElement>;
    return (
      <div
        ref={divRef}
        aria-disabled={disabled || undefined}
        className={cn(listRowRecipe(variants), className)}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...selectable}
        {...(props as React.HTMLAttributes<HTMLDivElement>)}
      />
    );
  }
);

export interface IconFactProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    IconFactRecipeProps,
    Pick<IconTileProps, 'icon' | 'tone' | 'iconTone' | 'shape'> {
  label: React.ReactNode;
  /** The value. A string gets body text; pass a node for anything richer. */
  value?: React.ReactNode;
  /** Tile size on the 4px grid. The overviews use 10; the job sheet 9. */
  size?: IconTileProps['size'];
  children?: React.ReactNode;
}

/**
 * A tinted icon tile beside a label-over-value pair.
 *
 *   <IconFact icon={Phone} tone="success" iconTone="success"
 *     label="Total calls" value="1,284" />
 *
 * Every integration overview and the job details sheet lay a fact out this
 * way; each wrote the tile, the `flex-1 min-w-0` column and the overline by
 * hand. `boxed` is the overviews' version, on a muted panel.
 */
export const IconFact = React.forwardRef<HTMLDivElement, IconFactProps>(
  function IconFact(
    {
      icon,
      tone,
      iconTone,
      shape,
      size = 10,
      label,
      value,
      boxed,
      className,
      children,
      ...props
    },
    ref
  ) {
    const content = children ?? value;
    return (
      <div
        ref={ref}
        className={cn(iconFactRecipe({ boxed }), className)}
        {...props}
      >
        <IconTile
          icon={icon}
          size={size}
          tone={tone}
          iconTone={iconTone}
          shape={shape}
        />
        <div
          className={`${uiMarkerInternal} flex min-w-0 flex-1 flex-col gap-1`}
        >
          <Text variant="overline" tone="muted">
            {label}
          </Text>
          {typeof content === 'string' || typeof content === 'number' ? (
            <Text variant="body-sm">{content}</Text>
          ) : (
            content
          )}
        </div>
      </div>
    );
  }
);
